import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ClipboardPaste, CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const TEMPLATE = {
  external_order_no: '',
  placed_at: '',
  items: [{ code: '', name: '', qty: 1, price: 0, notes: '' }],
  customer_name: '', customer_phone: '', delivery_address: '',
  currency: 'AED', subtotal: 0, discount: 0, tax: 0, delivery_fee: 0, total: 0,
  prepaid: true, notes: '', mode: 'order',
};

export default function DeliveryImport() {
  const [params] = useSearchParams();
  const [connectors, setConnectors] = useState([]);
  const [cid, setCid] = useState(params.get('connector') || '');
  const [jsonText, setJsonText] = useState(JSON.stringify(TEMPLATE, null, 2));
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get('/connectors').then((r) => {
      setConnectors(r.data);
      if (!cid && r.data.length) setCid(r.data[0].id);
    }).catch((e) => { if (e.response?.status === 403) setForbidden(true); });
  }, []);

  const parse = () => {
    try { return JSON.parse(jsonText); }
    catch (e) { toast.error(`Invalid JSON: ${e.message}`); return null; }
  };

  const validate = async () => {
    const payload = parse();
    if (!payload) return;
    if (!cid) { toast.error('Pick a connector first'); return; }
    setBusy('v'); setResult(null);
    try {
      const { data } = await api.post('/connectors/validate', { connector_id: cid, payload });
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(''); }
  };

  const submit = async () => {
    const payload = parse();
    if (!payload) return;
    setBusy('s');
    try {
      const { data } = await api.post('/connectors/console-submit', { connector_id: cid, payload });
      setResult(data);
      toast.success(data.duplicate ? 'Already submitted — deduplicated safely' : data.auto_accepted ? `Auto-added to POS: ${data.order_no}` : 'Submitted to Incoming Delivery Orders');
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setBusy(''); }
  };

  if (forbidden) return <div className="p-6 text-muted-foreground" data-testid="import-forbidden">Owner access only.</div>;

  const conn = connectors.find((c) => c.id === cid);

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ClipboardPaste className="h-6 w-6" /> Delivery Import Console</h1>
        <p className="text-sm text-muted-foreground">Owner-authorized manual/agent paste-in route for delivery orders (Claude pilot)</p>
      </div>
      <PageHelp id="delivery-import">Paste a structured order JSON (from Claude reading a portal, or typed manually), <b>Validate</b> to preview item mapping and totals without writing anything, then <b>Submit</b>. Duplicates are safely deduplicated by platform + external order ID. Every submission is audited.</PageHelp>

      <Card className="p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Connector (platform + outlet)</Label>
            <select className="w-full h-9 rounded-md border bg-background px-2" data-testid="import-connector" value={cid} onChange={(e) => { setCid(e.target.value); setReport(null); setResult(null); }}>
              {connectors.map((c) => <option key={c.id} value={c.id}>{c.platform_label} · {c.device_label}{c.paused ? ' (PAUSED)' : ''}</option>)}
            </select>
          </div>
          <div className="text-xs text-muted-foreground self-end">
            {conn && <>Mode: <b>{conn.integration_mode === 'claude' ? 'Claude-assisted pilot' : 'Chrome extension'}</b> · Status: <b>{conn.status_label}</b></>}
          </div>
        </div>
        <div>
          <Label>Order JSON</Label>
          <Textarea className="font-mono text-xs min-h-[280px]" data-testid="import-json" value={jsonText} onChange={(e) => { setJsonText(e.target.value); setReport(null); setResult(null); }} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="import-validate" disabled={busy === 'v'} onClick={validate}>
            {busy === 'v' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Validate (no writes)
          </Button>
          <Button data-testid="import-submit" disabled={!report?.valid || busy === 's'} onClick={submit}>
            {busy === 's' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowRight className="h-4 w-4 mr-1" />}Submit to POS
          </Button>
        </div>
      </Card>

      {report && (
        <Card className="p-4 space-y-2" data-testid="import-report">
          <div className="font-display font-semibold flex items-center gap-2">
            {report.valid ? <CheckCircle2 className="h-4 w-4 text-success" /> : <XCircle className="h-4 w-4 text-destructive" />}
            {report.valid ? 'Valid — safe to submit' : 'Fix these errors first'}
            {report.duplicate && <Badge variant="secondary">already submitted — will dedupe</Badge>}
          </div>
          {report.errors.map((e, i) => <div key={i} className="text-sm text-destructive flex gap-1.5"><XCircle className="h-4 w-4 shrink-0" />{e}</div>)}
          {report.warnings.map((w, i) => <div key={i} className="text-sm text-amber-600 flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" />{w}</div>)}
          {report.mapped_codes.length > 0 && <div className="text-sm">Mapped to POS items: <span className="font-mono">{report.mapped_codes.join(', ')}</span></div>}
          {report.unmapped_items.length > 0 && (
            <div className="text-sm text-amber-600">
              Unmapped (flagged for review, never substituted): {report.unmapped_items.map((i) => i.name || i.code || '?').join(', ')}
            </div>
          )}
        </Card>
      )}

      {result && (
        <Card className="p-4 space-y-1" data-testid="import-result">
          <div className="font-display font-semibold text-success">Submission acknowledged (persisted)</div>
          <div className="text-sm font-mono text-xs">inbox: {result.inbox_id}</div>
          {result.order_no && <div className="text-sm">POS order: <b>{result.order_no}</b> · KOT {result.kot_no} · AED {result.grand_total}</div>}
          <Link to="/aggregator" className="text-sm text-primary">Open Incoming Delivery Orders →</Link>
        </Card>
      )}
    </div>
  );
}
